import React, { useCallback, useMemo, useRef } from 'react'
import { VariableSizeList as List, ListChildComponentProps, areEqual } from 'react-window'
import AutoSizer from 'react-virtualized-auto-sizer'
import { useSelector } from 'react-redux'
import { Box, Flex } from 'rebass'
import { useNewTranslation } from 'hooks/index'
import { useResizeDetector } from 'react-resize-detector'
import { selectReviewPendingSubmission, selectShowTaskStepInfoInVerify } from 'state/review'
import { StepState, TaskState, selectStepUuidsByTaskUuid } from 'state/tasks'
import { BatchSubmissionState, BatchSubmissionTaskState } from 'state/submissions'
import { selectTasks, selectSubmitContentStepUuids } from 'state/tasks'
import { VerifyProgressBlock } from './ProgressBlock'
import { VerifyTabs } from './Verify'
import InWaitingListTooltip from './BatchProgressBlock/InWaitingListToolTip'

import Divider from 'components/Divider'
import EditRequestHistoryButton from './EditRequestHistory'
import CommentAndVoteSummary from './ProgressBlock/CommentAndVoteSummary'
import { spacing } from 'particles/styles'
import { coreColors } from 'particles/colors'
import Text from 'particles/Text'
import styled from 'styled-components'
import PartiposterProfile from '../PartiposterProfile'
import { useTheme } from 'particles/themes/styled'
import { HandleEditTagChange, HandleRejectTagChange } from 'components/utils/review'

const Cell: React.FC<ListChildComponentProps> = React.memo(({ index, style, data }) => {
    const [newT] = useNewTranslation()
    const { submissions, setRowHeight, handleEditTagChange, handleRejectTagChange, showFbAccount } = data
    const submission = submissions[index]
    const tasks = useSelector(selectTasks)
    const submitContentStepUuids = useSelector(selectSubmitContentStepUuids)
    const showTaskStepInfo = useSelector(selectShowTaskStepInfoInVerify)
    const rowRef = useRef<any>({})
    const theme = useTheme()
    const onResize = useCallback((width, height) => {
        setRowHeight(index, height)
    }, [])
    useResizeDetector({
        onResize,
        targetRef: rowRef,
        handleWidth: false,
        refreshMode: 'debounce',
        refreshRate: 800,
        observerOptions: { box: 'border-box' },
    })

    let fistDisplayTaskIndex = -1

    return (
        <Box style={style} key={submission.uuid}>
            {index !== 0 && <Divider key={`divider-${submission.uuid}`} my={spacing[300]} mx={spacing[300]} />}

            <Box ref={rowRef} bg={coreColors.white50}>
                {tasks.map((submitContentTask: TaskState, taskIndex: number) => {
                    const selectStepUuids = useSelector(selectStepUuidsByTaskUuid(submitContentTask.uuid))
                    const submissionTask =
                        submission.submission_tasks && submission.submission_tasks.length > 0
                            ? submission.submission_tasks.find(
                                  (t: BatchSubmissionTaskState) => t.task_uuid == submitContentTask.uuid,
                              )
                            : undefined
                    const step = submissionTask
                        ? submitContentTask.steps.find(
                              (s: StepState) =>
                                  submissionTask.current_progress &&
                                  s.uuid == submissionTask.current_progress.step_uuid,
                          )
                        : undefined

                    const stepIndex = step ? selectStepUuids.findIndex((stepUuid: string) => stepUuid == step.uuid) : -1

                    const submitContentStepOrderIndex = step
                        ? submitContentStepUuids.findIndex((stepUuid: string) => stepUuid == step.uuid)
                        : -1

                    if (
                        step == undefined ||
                        submitContentStepOrderIndex < 0 ||
                        submissionTask.status != 1 ||
                        (submissionTask.status == 1 &&
                            submissionTask.current_progress &&
                            submissionTask.current_progress.pending_action != null)
                    ) {
                        return null
                    } else {
                        // if is first display task, show profile
                        if (fistDisplayTaskIndex == -1) {
                            fistDisplayTaskIndex = taskIndex
                        }
                    }
                    return (
                        <div key={submissionTask.uuid}>
                            {fistDisplayTaskIndex < taskIndex && (
                                <Box bg={coreColors.white50}>
                                    <Divider
                                        mx={spacing[300]}
                                        my={spacing[300]}
                                        style={{ borderColor: theme.colors.border.secondary }}
                                    />
                                </Box>
                            )}
                            <Flex sx={{ gap: spacing[300] }}>
                                <Flex
                                    bg={coreColors.white50}
                                    flex={1}
                                    flexDirection="column"
                                    sx={{
                                        gap: spacing[400],
                                    }}
                                    p={spacing[300]}
                                >
                                    {fistDisplayTaskIndex == taskIndex && (
                                        <PartiposterProfile
                                            isVerifyMode
                                            submission={submission}
                                            submissionUuid={submission.uuid}
                                            options={{
                                                instagram: true,
                                                facebook: showFbAccount,
                                            }}
                                            TopRightComponent={
                                                <>
                                                    {submission.is_inside_batch != null &&
                                                        !submission.is_inside_batch && <InWaitingListTooltip />}
                                                </>
                                            }
                                        />
                                    )}

                                    {showTaskStepInfo && (
                                        <>
                                            <Box>
                                                <Text scale="bodyS500" textAlign="left">
                                                    {submitContentTask.title}
                                                </Text>

                                                <Box
                                                    width="95%"
                                                    sx={{
                                                        textOverflow: 'ellipsis',
                                                        overflow: 'hidden',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    <Text scale="caption400" textAlign="left">
                                                        {newT('taskType.stepNumberTitle', {
                                                            itemCount: stepIndex + 1,
                                                            firstItemCount: submitContentTask.steps
                                                                ? submitContentTask.steps.length
                                                                : 0,
                                                            lastItemCount:
                                                                step && step.data.title
                                                                    ? step.data.title
                                                                    : 'Step Title',
                                                        })}
                                                    </Text>
                                                </Box>
                                            </Box>
                                            <Divider
                                                my={0}
                                                sx={{ borderTopWidth: '2px', borderColor: coreColors.grey700 }}
                                            />
                                        </>
                                    )}
                                    <VerifyProgressBlock
                                        submissionProgress={submissionTask.current_progress}
                                        step={step}
                                        task={submitContentTask}
                                    />
                                    <Divider my={0} />

                                    <Box>
                                        <EditRequestHistoryButton
                                            submissionProgressUuid={submissionTask.current_progress.uuid}
                                            partiposterId={submission.partiposterId}
                                            fontSize="s"
                                        />
                                        <Box width="368px">
                                            <CommentAndVoteSummary
                                                step={step}
                                                submissionProgress={submissionTask.current_progress}
                                            />
                                        </Box>
                                    </Box>
                                </Flex>
                                <Box
                                    bg={theme.colors.surface.primary}
                                    width="450px"
                                    px={spacing[300]}
                                    py={spacing[500]}
                                >
                                    <VerifyTabs
                                        submissionUuid={submission.uuid}
                                        submissionProgress={submissionTask.current_progress}
                                        step={step}
                                        task={submitContentTask}
                                        handleEditTagChange={handleEditTagChange}
                                        handleRejectTagChange={handleRejectTagChange}
                                        isInsideBatch={submission.is_inside_batch == null || submission.is_inside_batch}
                                    />
                                </Box>
                            </Flex>
                        </div>
                    )
                })}
            </Box>
        </Box>
    )
}, areEqual)
Cell.displayName = 'Cell'

interface VerifyAllTasksProps {
    handleEditTagChange: HandleEditTagChange
    handleRejectTagChange: HandleRejectTagChange
    submissions: BatchSubmissionState[]
}

const VerifyAllTasks: React.FC<VerifyAllTasksProps> = ({ submissions, handleEditTagChange, handleRejectTagChange }) => {
    const reviewPendingSubmission = useSelector(selectReviewPendingSubmission)
    const tasks = useSelector(selectTasks)
    const showFbAccount = useMemo(
        () =>
            tasks.some(task => {
                return task.platform == 2
            }),
        tasks,
    )

    const sortedSubmissions = useMemo(() => {
        if (reviewPendingSubmission) {
            const firstSubmissionIndex = submissions.findIndex(s => s.uuid === reviewPendingSubmission)
            const result = [...submissions]
            if (firstSubmissionIndex > -1) {
                result.splice(0, 0, result.splice(firstSubmissionIndex, 1)[0])
                return result
            }
        }
        return submissions
    }, [reviewPendingSubmission, submissions])

    const rowHeights = useRef<any>({})
    const listRef = useRef<any>({})

    const getRowHeight = (index: number): number => {
        return rowHeights.current[index] + 15 || 2600
    }

    const setRowHeight = (index: number, size: number): void => {
        listRef.current.resetAfterIndex(0)
        rowHeights.current = { ...rowHeights.current, [index]: size }
    }
    return (
        <FlexBox>
            <AutoSizer>
                {({ height, width }: { height: number; width: number }) => (
                    <List
                        ref={listRef}
                        height={height}
                        width={width}
                        itemCount={sortedSubmissions.length}
                        itemData={{
                            submissions: sortedSubmissions,
                            setRowHeight,
                            handleEditTagChange,
                            handleRejectTagChange,
                            showFbAccount,
                        }}
                        itemSize={getRowHeight}
                    >
                        {Cell}
                    </List>
                )}
            </AutoSizer>
        </FlexBox>
    )
}

export default VerifyAllTasks

const FlexBox = styled.div`
    box-sizing: border-box;
    display: flex;
    flex: 1 1 auto;
    padding-top: ${spacing[300]};
    overflow-y: hidden;

    @media (max-width: 1280px) {
        min-width: 1111px;
    }
`
